<template>
    <div class="invoice-pennylane-sync">
        <!-- Message si la facture ne peut pas être synchronisée -->
        <div v-if="!canBeSynced" class="alert alert-warning mb-0">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Synchronisation non disponible</strong>
            <p class="mb-0 mt-2">Cette facture ne peut pas être synchronisée  (facture en brouillon).</p>
        </div>

        <div v-else-if="!invoice.is_valid" class="alert alert-danger mb-0">
            <i class="fas fa-exclamation-circle mr-2"></i>
            <strong>Informations manquantes</strong>
            <p class="mb-0 mt-2">Cette facture ne peut pas être synchronisée car certaines informations obligatoires sont manquantes (SIREN, TVA intracommunautaire, adresse de facturation ou paramètres de l'entreprise).</p>
        </div>

        <div v-else>
            <!-- Statut de synchronisation -->
            <div v-if="!invoice.pennylane_id" class="alert alert-info mb-3">
                <i class="fas fa-info-circle mr-2"></i>
                <strong>Facture non synchronisée</strong>
                <p class="mb-0 mt-1 small">Cette facture n'a pas encore été synchronisée.</p>
            </div>

            <div v-else class="sync-status mb-3 p-3 bg-light rounded">
                <div class="status-item">
                    <i class="fas fa-check-circle text-success mr-2"></i>
                    <span class="font-weight-bold">Facture e-synchronisée</span>
                    <small class="d-block text-muted ml-4 mt-1">ID : {{ invoice.pennylane_id }}</small>
                </div>
                <div v-if="invoice.pennylane_status" class="status-item mt-2 pt-2 border-top">
                    <i class="fas mr-2" :class="invoice.is_pennylane_awaiting_transmission ? 'fa-exclamation-triangle text-warning' : getStatusIcon(invoice.pennylane_status)"></i>
                    <span>Statut : <strong>{{ invoice.is_pennylane_awaiting_transmission ? 'Non transmise' : getStatusLabel(invoice.pennylane_status) }}</strong></span>
                </div>
                <div v-if="invoice.pennylane_synced_at" class="status-item mt-2 pt-2 border-top">
                    <i class="fas fa-clock text-muted mr-2"></i>
                    <span class="text-muted">Dernière synchronisation : {{ formatSyncDate(invoice.pennylane_synced_at) }}</span>
                </div>
            </div>

            <!-- Avertissement si facture finalisée -->
            <div v-if="invoice.is_pennylane_finalized" class="alert alert-info mb-3">
                <i class="fas fa-lock mr-2"></i>
                <strong>Facture e-transmise</strong>
                <p class="mb-0 mt-1 small">
                    Cette facture est e-transmise avec votre PA et ne peut plus être modifiée.
                </p>
            </div>

            <!-- Importée chez Pennylane mais pas transmise à la plateforme -->
            <div v-else-if="invoice.is_pennylane_awaiting_transmission" class="alert alert-warning mb-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <strong>Facture non transmise</strong>
                <p class="mb-0 mt-1 small">
                    Elle est dans Pennylane mais n'a pas été envoyée à la plateforme (motif dans l'historique).
                    Si le Factur-X est refusé : corrigez, archivez la facture dans Pennylane, puis synchronisez.
                </p>
            </div>

            <!-- Avertissement de re-synchronisation nécessaire -->
            <div v-else-if="invoice.needs_pennylane_resync" class="alert alert-warning mb-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <strong>Attention</strong>
                <p class="mb-0 mt-1 small">
                    Les données de cette facture ont été modifiées depuis la dernière synchronisation.
                    Il est recommandé de mettre à jour les données dans PennyLane.
                </p>
                <div v-if="invoice.pennylane_modified_fields && invoice.pennylane_modified_fields.length > 0" class="mt-2 pt-2 border-top">
                    <strong class="small">Champs modifiés :</strong>
                    <ul class="mb-0 mt-1 pl-3">
                        <li v-for="field in invoice.pennylane_modified_fields" :key="field" class="small">
                            {{ field }}
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Boutons d'action -->

             
            <div class="sync-actions mb-3">
                <button 
                    v-show="!invoice.is_pennylane_finalized"
                    class="btn btn-primary btn-block"
                    @click="syncInvoice"
                    :disabled="syncing || finalizing || invoice.is_pennylane_finalized"
                >
                    <i class="fas" :class="syncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                    Synchroniser
                </button>
                   <!--
                <button 
                    v-if="invoice.pennylane_id && !invoice.is_pennylane_finalized"
                    class="btn btn-success btn-block mt-2"
                    @click="showFinalizeModal = true"
                    :disabled="syncing || finalizing"
                >
                    <i class="fas" :class="finalizing ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
                    E-transmettre
                </button>
                -->
            </div>

            <!-- Modal de confirmation de finalisation -->
            <div class="modal fade" :class="{ 'show d-block': showFinalizeModal }" tabindex="-1" role="dialog" v-if="showFinalizeModal">
                <div class="modal-dialog modal-dialog-centered" role="document">
                    <div class="modal-content">
                        <div class="modal-header bg-warning text-white">
                            <h5 class="modal-title">
                                <i class="fas fa-exclamation-triangle mr-2"></i>
                                Confirmation d'e-transmission
                            </h5>
                            <button type="button" class="close text-white" @click="showFinalizeModal = false">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div class="modal-body">
                            <div class="alert alert-warning mb-3">
                                <i class="fas fa-info-circle mr-2"></i>
                                <strong>Action irréversible</strong>
                            </div>
                            <p class="mb-3">
                                Vous êtes sur le point de <strong>e-transmettre cette facture</strong>.
                            </p>
                            <div class="bg-light p-3 rounded mb-3">
                                <h6 class="font-weight-bold mb-2">
                                    <i class="fas fa-lock mr-2 text-danger"></i>
                                    Conséquences :
                                </h6>
                                <ul class="mb-0 pl-4">
                                    <li class="mb-2">La facture ne pourra <strong>plus être modifiée</strong></li>
                                    <li class="mb-2">Les modifications locales ne seront <strong>plus synchronisées</strong></li>
                                    <li>La facture sera considérée comme <strong>définitive</strong></li>
                                </ul>
                            </div>
                            <p class="text-muted small mb-0">
                                <i class="fas fa-question-circle mr-1"></i>
                                Assurez-vous que toutes les informations de la facture sont correctes avant de continuer.
                            </p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" @click="showFinalizeModal = false" :disabled="finalizing">
                                <i class="fas fa-times mr-1"></i>
                                Annuler
                            </button>
                            <button type="button" class="btn btn-success" @click="confirmFinalize" :disabled="finalizing">
                                <i class="fas mr-1" :class="finalizing ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
                                {{ finalizing ? 'Finalisation en cours...' : 'Confirmer la finalisation' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-backdrop fade" :class="{ 'show': showFinalizeModal }" v-if="showFinalizeModal"></div>

            <!-- Informations sur le client -->
            <div class="sync-info mb-3 p-3 bg-light rounded">
                <h6 class="font-weight-bold mb-2">
                    <i class="fas fa-info-circle mr-2"></i>Informations
                </h6>
                <div class="small">
                    <div class="mb-2">
                        <strong>Client :</strong> {{ invoice.company_name }}
                        <span v-if="invoice.company_pennylane_id" class="text-success ml-2">
                            <i class="fas fa-check-circle"></i> Synchronisé
                        </span>
                        <span v-else class="text-warning ml-2">
                            <i class="fas fa-exclamation-triangle"></i> Non synchronisé
                        </span>
                    </div>
                    <div v-if="!invoice.company_pennylane_id" class="text-muted">
                        Le client sera automatiquement synchronisé lors de la synchronisation de la facture.
                    </div>
                </div>
            </div>

            <!-- Résultat de la synchronisation -->
            <div v-if="result">
                <div v-if="result.success" class="alert alert-success mb-0">
                    <i class="fas fa-check-circle mr-2"></i>
                    Synchronisation réussie !
                </div>

                <div v-else class="alert alert-danger mb-0">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Erreur lors de la synchronisation</strong>
                    <div class="mt-2">{{ result.error }}</div>
                </div>
            </div>

            <!-- Historique des logs -->
            <div v-if="logs.length > 0" class="sync-logs mt-4">
                <hr class="my-4">
                <h6 class="font-weight-bold mb-3">
                    <i class="fas fa-history mr-2"></i>
                    Historique de synchronisation
                </h6>
                <div class="list-group">
                    <div v-for="log in logs" :key="log.id" class="list-group-item">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="flex-grow-1">
                                <div class="d-flex align-items-center mb-1">
                                    <i class="fas mr-2" :class="log.status === 'success' ? 'fa-check-circle text-success' : 'fa-exclamation-circle text-danger'"></i>
                                    <strong>{{ getActionLabel(log.action) }}</strong>
                                    <span class="badge ml-2" :class="log.status === 'success' ? 'badge-success' : 'badge-danger'">
                                        {{ log.status === 'success' ? 'Succès' : 'Erreur' }}
                                    </span>
                                </div>
                                <p class="mb-1 small text-muted">{{ log.message }}</p>
                                <small class="text-muted">
                                    <i class="fas fa-clock mr-1"></i>
                                    {{ formatLogDate(log.created_at) }}
                                </small>

                                <!-- Détails des modifications -->
                                <div v-if="log.details && log.status == 'success'" class="mt-2 small">
                                    <!-- Champs modifiés -->
                                    <div v-if="log.details.modified_fields && log.details.modified_fields.length > 0" class="mb-2">
                                        <span class="text-info">
                                            <i class="fas fa-info-circle mr-1"></i>
                                            {{ log.details.modified_fields.join(', ') }}
                                        </span>
                                    </div>

                                    <!-- Détails des lignes -->
                                    <div v-if="log.details.lines" class="mt-2 p-2 bg-light rounded">
                                        <strong class="d-block mb-2"><i class="fas fa-list mr-1"></i>Détails des lignes :</strong>

                                        <!-- Lignes ajoutées -->
                                        <div v-if="log.details.lines.added && log.details.lines.added.length > 0" class="mb-2">
                                            <span class="badge badge-success">+{{ log.details.lines.added.length }} ajoutée(s)</span>
                                            <ul class="mb-0 mt-1 pl-3">
                                                <li v-for="(line, index) in log.details.lines.added" :key="'added-' + index">
                                                    {{ line.designation }} (Qté: {{ line.quantity }}, {{ formatAmount(line.amount) }}€)
                                                </li>
                                            </ul>
                                        </div>

                                        <!-- Lignes modifiées -->
                                        <div v-if="log.details.lines.modified && log.details.lines.modified.length > 0" class="mb-2">
                                            <span class="badge badge-warning">~{{ log.details.lines.modified.length }} modifiée(s)</span>
                                            <ul class="mb-0 mt-1 pl-3">
                                                <li v-for="(line, index) in log.details.lines.modified" :key="'modified-' + index">
                                                    <strong>{{ line.designation }}</strong>
                                                    <ul class="mb-0 pl-3">
                                                        <li v-for="(change, field) in line.changes" :key="field" class="text-muted">
                                                            {{ ucfirst(field) }}: 
                                                            <span class="text-danger">{{ formatValue(change.old) }}</span>
                                                            → 
                                                            <span class="text-success">{{ formatValue(change.new) }}</span>
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </div>

                                        <!-- Lignes supprimées -->
                                        <div v-if="log.details.lines.deleted && log.details.lines.deleted.length > 0" class="mb-0">
                                            <span class="badge badge-danger">-{{ log.details.lines.deleted.length }} supprimée(s)</span>
                                            <ul class="mb-0 mt-1 pl-3">
                                                <li v-for="(line, index) in log.details.lines.deleted" :key="'deleted-' + index">
                                                    {{ line.designation }}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Utilisateur -->
                            <div v-if="log.user" class="text-right ml-3">
                                <small class="text-muted">{{ log.user.fullname }}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InvoicePennylaneSync',
    
    props: {
        invoiceId: {
            type: Number,
            required: true
        },
        initialInvoice: {
            type: Object,
            default: () => ({})
        },
        initialLogs: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            invoice: { ...this.initialInvoice },
            logs: [...this.initialLogs],
            syncing: false,
            finalizing: false,
            markingPaid: false,
            showFinalizeModal: false,
            result: null
        };
    },

    computed: {
        canBeSynced() {
            // Peut synchroniser uniquement si la facture est validée (state != STATE_PROGRESS = 1)
            return this.invoice.state !== 1;
        }
    },

    methods: {
        syncInvoice() {
            this.syncing = true;
            this.result = null;

            fetch(`/api/invoices/${this.invoiceId}/pennylane/sync`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = data;

                        if (data.success) {
                            // Mettre à jour les données locales
                            this.invoice.pennylane_id = data.pennylane_id;
                            this.invoice.pennylane_synced_at = data.pennylane_synced_at;
                            this.invoice.needs_pennylane_resync = false;

                            // Recharger la page après 2 secondes pour mettre à jour toutes les données
                            setTimeout(() => {
                                window.location.reload();
                            }, 2000);
                        }
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de la synchronisation'
                        };
                    });
                }
                this.syncing = false;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };

                this.$toasted.error('Erreur réseau lors de la synchronisation', {
                    duration: 5000
                });
                this.syncing = false;
            });
        },

        markAsPaid() {
            this.markingPaid = true;
            this.result = null;

            fetch(`/api/invoices/${this.invoiceId}/pennylane/mark-paid`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = {
                            success: true,
                            message: 'Facture encaissée avec succès dans PennyLane'
                        };

                        // Mettre à jour les données locales
                        this.invoice.pennylane_status = data.pennylane_status;

                        // Recharger la page après 2 secondes
                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de l\'encaissement'
                        };
                    });
                }
                this.markingPaid = false;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };
                this.markingPaid = false;
            });
        },

        confirmFinalize() {
            this.finalizing = true;
            this.result = null;
            this.showFinalizeModal = false;

            fetch(`/api/invoices/${this.invoiceId}/pennylane/finalize`, {
                ...{
                    method: "POST"
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.result = {
                            success: true,
                            message: 'Facture finalisée avec succès dans PennyLane'
                        };

                        // Mettre à jour les données locales
                        this.invoice.pennylane_status = data.pennylane_status;
                        this.invoice.is_pennylane_finalized = true;

                        // Recharger la page après 2 secondes
                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    });
                } else {
                    response.json().then((data) => {
                        this.result = {
                            success: false,
                            error: data.error || 'Erreur lors de la finalisation'
                        };
                    });
                }
                this.finalizing = false;
            }).catch((error) => {
                console.log(error);
                this.result = {
                    success: false,
                    error: error.message
                };

                this.$toasted.error('Erreur réseau lors de la finalisation', {
                    duration: 5000
                });
                this.finalizing = false;
            });
        },

        getStatusLabel(status) {
            const labels = {
                1: 'Brouillon',      // PENNYLANE_STATUS_DRAFT
                2: 'Finalisée',      // PENNYLANE_STATUS_FINALIZED
                3: 'Payée'           // PENNYLANE_STATUS_PAID
            };
            return labels[status] || status;
        },

        getStatusIcon(status) {
            const icons = {
                1: 'fa-file-alt text-warning',      // PENNYLANE_STATUS_DRAFT
                2: 'fa-lock text-info',              // PENNYLANE_STATUS_FINALIZED
                3: 'fa-check-circle text-success'    // PENNYLANE_STATUS_PAID
            };
            return icons[status] || 'fa-info-circle';
        },

        formatSyncDate(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return 'à l\'instant';
            } else if (diffMins < 60) {
                return `il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
            } else if (diffHours < 24) {
                return `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
            } else if (diffDays < 7) {
                return `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
            } else {
                return date.toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        },

        getActionLabel(action) {
            const labels = {
                'create': 'Création',
                'update': 'Mise à jour',
                'finalize': 'Finalisation',
                'mark_paid': 'Encaissement'
            };
            return labels[action] || action;
        },

        formatLogDate(date) {
            return this.moment(date).format('DD/MM/YYYY à HH:mm');
        },

        formatAmount(amount) {
            return parseFloat(amount).toLocaleString('fr-FR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        formatValue(value) {
            if (typeof value === 'number') {
                return this.formatAmount(value);
            }
            return value;
        },

        ucfirst(string) {
            if (!string) return '';
            return string.charAt(0).toUpperCase() + string.slice(1);
        }
    }
};
</script>

<style scoped>
.invoice-pennylane-sync {
    padding: 0;
}

.sync-status {
    border-left: 3px solid #28a745;
}

.sync-status .status-item {
    padding: 4px 0;
}

.sync-actions .btn {
    font-size: 15px;
    padding: 12px 20px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.sync-actions .btn i {
    margin-right: 8px;
}

.sync-actions .btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.sync-actions .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.alert {
    font-size: 14px;
    padding: 15px;
    border-radius: 6px;
}

.alert strong {
    font-size: 15px;
}

.alert.alert-info {
    background-color: #e7f3ff;
    border-color: #b3d9ff;
    color: #004085;
}

.alert.alert-warning {
    background-color: #fff3cd;
    border-color: #ffc107;
    color: #856404;
}

.alert.alert-success {
    background-color: #d4edda;
    border-color: #28a745;
    color: #155724;
}

.alert.alert-danger {
    background-color: #f8d7da;
    border-color: #dc3545;
    color: #721c24;
}

.small {
    font-size: 13px;
}

.sync-info {
    border-left: 3px solid #17a2b8;
}
</style>
